import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { Children } from '@/utils/reactTypes';
import { Box, Button, Divider, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { forwardRef } from 'react';

import { BandFieldSpec, FIELD_GAP_PX, useVisibleFieldCount } from './useVisibleFieldCount';

export const BAND_HEIGHT_PX = 76;
const IDENTITY_MAX_WIDTH_PX = 320;

type StaticBandField = Omit<BandFieldSpec, 'value'>;

// The fields every patient has, so their labels can show before the patient loads.
export const EXTERNAL_ID_FIELD: StaticBandField = { fieldKey: 'externalId', label: 'External ID' };
export const PHONE_FIELD: StaticBandField = { fieldKey: 'phone', label: 'Phone' };
export const LANGUAGE_FIELD: StaticBandField = { fieldKey: 'language', label: 'Language' };
export const ADDRESS_FIELD: StaticBandField = {
    fieldKey: 'address',
    label: 'Address',
    canWrap: true,
};

export const BandSurface = ({ children }: { children: Children }) => (
    <Box
        sx={(theme) => ({
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            minHeight: `${BAND_HEIGHT_PX}px`,
            display: 'flex',
            // A chip's default fill and text are dark, so on the band they take a
            // light-on-dark palette.
            '& .MuiChip-root': {
                bgcolor: alpha(theme.palette.secondary.contrastText, 0.16),
                color: theme.palette.secondary.contrastText,
                '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus': {
                    bgcolor: alpha(theme.palette.secondary.contrastText, 0.26),
                },
            },
            '& .MuiChip-deleteIcon': {
                color: alpha(theme.palette.secondary.contrastText, 0.7),
                '&:hover': { color: theme.palette.secondary.contrastText },
            },
            '& .MuiSkeleton-root': {
                bgcolor: alpha(theme.palette.secondary.contrastText, 0.16),
            },
        })}
    >
        {children}
    </Box>
);

export const BandField = ({
    label,
    value,
    canWrap = false,
    hidden = false,
    wrapped = false,
}: BandFieldSpec & { hidden?: boolean; wrapped?: boolean }) => (
    <VerticalStack
        sx={{
            display: hidden ? 'none' : undefined,
            flexShrink: 0,
            width: canWrap && wrapped ? 'min-content' : undefined,
        }}
    >
        <Typography fontSize='14px' lineHeight='20px' fontWeight={500} noWrap>
            {label}
        </Typography>
        <Typography
            component='div'
            noWrap={!canWrap}
            fontSize='14px'
            lineHeight='20px'
            sx={(theme) => ({ color: alpha(theme.palette.secondary.contrastText, 0.85) })}
        >
            {value}
        </Typography>
    </VerticalStack>
);

export const BandRow = ({ children }: { children: Children }) => (
    <HorizontalStack useFlexGap sx={{ px: 3, flex: 1, minWidth: 0, alignItems: 'center' }}>
        {children}
    </HorizontalStack>
);

export const BandIdentity = ({ children }: { children: Children }) => (
    <Box
        sx={{
            flexShrink: 0,
            minWidth: 0,
            maxWidth: `${IDENTITY_MAX_WIDTH_PX}px`,
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        {children}
    </Box>
);

export const BandTitle = ({ children }: { children: Children }) => (
    <HorizontalStack sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>{children}</HorizontalStack>
);

export const BandSubtitle = ({ children }: { children: Children }) => (
    <Typography component='div' fontSize='14px' lineHeight='20px' noWrap>
        {children}
    </Typography>
);

const BandDivider = ({ hidden = false }: { hidden?: boolean }) => (
    <Divider
        orientation='vertical'
        flexItem
        sx={(theme) => ({
            mx: 3,
            visibility: hidden ? 'hidden' : undefined,
            borderColor: alpha(theme.palette.secondary.contrastText, 0.2),
        })}
    />
);

// Takes whatever the name and the button leave, so the room the fields have does not depend on
// how many of them are showing.
const BandFields = forwardRef<HTMLDivElement, { children: Children }>(({ children }, ref) => (
    <Box
        ref={ref}
        sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'flex-start',
            overflow: 'hidden',
            columnGap: `${FIELD_GAP_PX}px`,
        }}
    >
        {children}
    </Box>
));
BandFields.displayName = 'BandFields';

// Fields that do not fit are dropped whole from the end, the same way whether they hold values or
// placeholders.
export const MeasuredBandFields = ({ fields }: { fields: BandFieldSpec[] }) => {
    const { rowRef, visibleCount, wrapped } = useVisibleFieldCount(fields);

    return (
        <>
            <BandDivider hidden={visibleCount === 0} />
            <BandFields ref={rowRef}>
                {fields.map((field, index) => (
                    <BandField
                        key={field.fieldKey}
                        {...field}
                        hidden={index >= visibleCount}
                        wrapped={wrapped}
                    />
                ))}
            </BandFields>
        </>
    );
};

// View More is disabled until there is a patient to open.
export const ViewMoreButton = ({ onClick }: { onClick?: () => void }) => (
    <Button
        variant='outlined'
        size='small'
        onClick={onClick}
        disabled={!onClick}
        sx={(theme) => ({
            flexShrink: 0,
            color: theme.palette.secondary.contrastText,
            borderColor: alpha(theme.palette.secondary.contrastText, 0.6),
            '&:hover': {
                borderColor: theme.palette.secondary.contrastText,
                bgcolor: alpha(theme.palette.secondary.contrastText, 0.08),
            },
            '&.Mui-disabled': {
                color: alpha(theme.palette.secondary.contrastText, 0.5),
                borderColor: alpha(theme.palette.secondary.contrastText, 0.3),
            },
        })}
    >
        View More
    </Button>
);
